/**
 * Civic Data AI Crawler — DEMO MODE
 *
 * Single page wired to 3 routes (selectable tab → /demo/civic-data-search,
 *  /demo/budget-crawler, /demo/public-records).
 *
 * Strictly demo: every payload returns is_demo=true. Backend is a deterministic,
 * hash-seeded simulation — no real crawler is hit.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  Search, Database, FileText, Bot, ShieldCheck, ArrowRight,
  AlertTriangle, CheckCircle2, Loader2, Globe, ScanLine,
  TrendingUp, FileSearch,
} from "lucide-react";

// ---------- Tab routes ----------
const TABS = [
  { id: "search",  to: "/demo/civic-data-search", label: "Civic Data Search",  Icon: Search },
  { id: "budget",  to: "/demo/budget-crawler",     label: "Budget Crawler",     Icon: TrendingUp },
  { id: "records", to: "/demo/public-records",     label: "Public Records",     Icon: FileSearch },
];

const SEVERITY_TONE = {
  low:      { color: "text-[color:var(--auditiq-success)]", bg: "bg-[color:var(--auditiq-success)]/10", border: "border-[color:var(--auditiq-success)]/40" },
  watch:    { color: "text-[color:var(--auditiq-warning)]", bg: "bg-[color:var(--auditiq-warning)]/10", border: "border-[color:var(--auditiq-warning)]/40" },
  moderate: { color: "text-[color:var(--auditiq-warning)]", bg: "bg-[color:var(--auditiq-warning)]/10", border: "border-[color:var(--auditiq-warning)]/40" },
  high:     { color: "text-[color:var(--auditiq-critical)]", bg: "bg-[color:var(--auditiq-critical)]/10", border: "border-[color:var(--auditiq-critical)]/40" },
};

const RiskPill = ({ risk }) => {
  if (!risk) return null;
  const t = SEVERITY_TONE[risk.severity] || SEVERITY_TONE.watch;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${t.bg} ${t.color} ${t.border} border`}
      data-testid={`risk-pill-${risk.severity}`}
    >
      <span className="pulse-dot" style={{ background: "currentColor" }} />
      {risk.label} · {risk.score}
    </span>
  );
};

const Stat = ({ label, value, accent = "gold" }) => (
  <div className="card-premium glass">
    <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{label}</div>
    <div className={`font-display text-3xl mt-1 text-${accent}`}>{value}</div>
  </div>
);

const NarrativeBlock = ({ title, text, testid }) => (
  <div className="card-premium glass border-l-2 border-electric mt-6" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-electric flex items-center gap-2">
      <Bot size={12} /> {title}
    </div>
    <p className="text-cream-dim mt-3 leading-relaxed">{text}</p>
  </div>
);

const DemoNotice = () => (
  <div className="card-premium glass-accent flex items-start gap-3 mt-6" data-testid="demo-notice">
    <ShieldCheck size={18} className="text-electric mt-1 shrink-0" />
    <div className="text-cream-dim text-sm">
      <span className="text-electric font-medium">Demo mode:</span> All results are
      simulated. No live websites are crawled — every response is a deterministic
      AuditIQ classifier output for training and showcase purposes. Findings use
      non-accusatory framing only ("variance", "review recommended", "worth watching").
    </div>
  </div>
);

const DemoCivicCrawler = () => {
  const loc = useLocation();
  const nav = useNavigate();

  const activeTab = useMemo(() => {
    const t = TABS.find((x) => x.to === loc.pathname);
    return t ? t.id : "search";
  }, [loc.pathname]);

  const [meta, setMeta] = useState({ jurisdictions: [], record_types: [], budget_categories: [] });
  const [jurisdiction, setJurisdiction] = useState("port-st-lucie-fl");
  const [query, setQuery] = useState("");
  const [fy, setFy] = useState("FY26");
  const [recordType, setRecordType] = useState("");
  const [keyword, setKeyword] = useState("");

  const [searchData, setSearchData] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [recordsData, setRecordsData] = useState(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/demo/civic/jurisdictions")
      .then((r) => setMeta(r.data))
      .catch(() => toast.error("Could not load demo jurisdictions"));
  }, []);

  const runSearch = async () => {
    setLoading(true); setSearchData(null);
    try {
      const r = await api.post("/demo/civic/search", { jurisdiction, query });
      setSearchData(r.data);
    } catch (e) { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  const runBudget = async () => {
    setLoading(true); setBudgetData(null);
    try {
      const r = await api.post("/demo/civic/budget-crawl", { jurisdiction, fiscal_year: fy });
      setBudgetData(r.data);
    } catch (e) { toast.error("Budget crawl failed"); }
    finally { setLoading(false); }
  };

  const runRecords = async () => {
    setLoading(true); setRecordsData(null);
    try {
      const r = await api.post("/demo/civic/public-records", { jurisdiction, record_type: recordType, keyword });
      setRecordsData(r.data);
    } catch (e) { toast.error("Public records lookup failed"); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <SEOHead
        title="Civic Data AI Crawler · Demo | AuditIQ"
        description="Interactive demo of the AuditIQ civic data crawler — public budgets, public records, and AI risk scoring. Robots.txt compliant. Deterministic simulation."
        path={loc.pathname}
      />
      <Toaster position="top-right" richColors theme="dark" />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="civic-demo-hero">
        <div className="absolute inset-0 cyber-grid opacity-30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(45,156,219,0.18),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(201,168,76,0.12),transparent_55%)]" />
        <div className="container-x relative section-y">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric flex items-center gap-2">
            <ScanLine size={12} /> Civic Data Intelligence
          </div>
          <h1 className="font-display mt-4">AI Civic Data Crawler — Demo</h1>
          <p className="text-cream-dim mt-4 max-w-2xl">
            Simulate AuditIQ's public-data crawler at work. Pick a jurisdiction
            and watch the engine surface budget anomalies, public records, and
            AI-graded risk indicators — without ever leaving demo mode.
          </p>
          <DemoNotice />
        </div>
      </section>

      {/* TABS */}
      <section className="bg-navy-deep border-y border-gold-dim/20">
        <div className="container-x flex gap-1 overflow-x-auto" data-testid="civic-demo-tabs">
          {TABS.map(({ id, to, label, Icon }) => {
            const active = id === activeTab;
            return (
              <button
                key={id}
                onClick={() => nav(to)}
                data-testid={`civic-tab-${id}`}
                className={`flex items-center gap-2 px-5 py-4 text-sm uppercase tracking-[0.18em] whitespace-nowrap transition-colors border-b-2 ${
                  active ? "text-gold border-gold" : "text-cream-dim border-transparent hover:text-gold"
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            );
          })}
        </div>
      </section>

      {/* CONTROLS + RESULTS */}
      <section className="container-x section-y">
        {/* Shared jurisdiction picker */}
        <div className="card-premium glass" data-testid="civic-controls">
          <div className="grid md:grid-cols-2 gap-4 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel mb-2">Jurisdiction</div>
              <Select value={jurisdiction} onValueChange={setJurisdiction}>
                <SelectTrigger
                  className="bg-navy-deep border-gold-dim/40 text-cream"
                  data-testid="civic-jurisdiction-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                  {Array.from(new Map((meta.jurisdictions || []).map((j) => [j.id, j])).values()).map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activeTab === "search" && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-steel mb-2">Query (optional)</div>
                <Input
                  placeholder="e.g. drainage capital project"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="bg-navy-deep border-gold-dim/40 text-cream"
                  data-testid="civic-search-query"
                />
              </div>
            )}

            {activeTab === "budget" && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-steel mb-2">Fiscal Year</div>
                <Select value={fy} onValueChange={setFy}>
                  <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="civic-fy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                    {["FY24", "FY25", "FY26", "FY27"].map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {activeTab === "records" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-steel mb-2">Record type</div>
                  <Select value={recordType} onValueChange={setRecordType}>
                    <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="civic-record-type">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                      <SelectItem value=" ">Any record type</SelectItem>
                      {(meta.record_types || []).map((rt) => <SelectItem key={rt} value={rt}>{rt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-steel mb-2">Keyword</div>
                  <Input
                    placeholder="e.g. vendor"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    className="bg-navy-deep border-gold-dim/40 text-cream"
                    data-testid="civic-record-keyword"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-5 btn-stack">
            {activeTab === "search" && (
              <Button onClick={runSearch} disabled={loading} className="btn-gold h-12" data-testid="civic-run-search">
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Search size={14} className="mr-2" />}
                Run Civic Crawl
              </Button>
            )}
            {activeTab === "budget" && (
              <Button onClick={runBudget} disabled={loading} className="btn-gold h-12" data-testid="civic-run-budget">
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <TrendingUp size={14} className="mr-2" />}
                Crawl Budget
              </Button>
            )}
            {activeTab === "records" && (
              <Button onClick={runRecords} disabled={loading} className="btn-gold h-12" data-testid="civic-run-records">
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <FileSearch size={14} className="mr-2" />}
                Search Records
              </Button>
            )}
          </div>
        </div>

        {/* ===== SEARCH RESULTS ===== */}
        {activeTab === "search" && searchData && (
          <div className="mt-8" data-testid="civic-search-results">
            <div className="grid sm:grid-cols-3 gap-4">
              <Stat label="Sources crawled" value={searchData.sources_crawled.length} accent="gold" />
              <Stat label="Documents indexed" value={searchData.sources_crawled.reduce((a, s) => a + s.docs, 0).toLocaleString()} accent="electric" />
              <div className="card-premium glass">
                <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Composite risk</div>
                <div className="mt-2"><RiskPill risk={searchData.ai_risk} /></div>
              </div>
            </div>
            <NarrativeBlock title="AuditIQ AI Narrative" text={searchData.narrative} testid="civic-search-narrative" />

            <h3 className="font-display mt-8">Sources</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              {searchData.sources_crawled.map((s, i) => (
                <div key={i} className="glass p-4">
                  <div className="flex items-center gap-2"><Globe size={14} className="text-gold" /><span className="text-cream text-sm">{s.source}</span></div>
                  <div className="mt-3 flex justify-between text-xs">
                    <span className="text-cream-dim">{s.docs.toLocaleString()} docs</span>
                    <span className="text-electric flex items-center gap-1"><CheckCircle2 size={12} /> robots.txt</span>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="font-display mt-10">Top matches</h3>
            <div className="space-y-3 mt-4">
              {searchData.matches.map((m) => (
                <article key={m.id} className="glass p-5" data-testid={`civic-match-${m.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{m.record_type} · {m.category}</div>
                      <h4 className="font-display text-cream mt-1">{m.title}</h4>
                      <p className="text-cream-dim text-sm mt-2">{m.snippet}</p>
                      <div className="text-xs text-electric mt-2">{m.source}</div>
                    </div>
                    <RiskPill risk={m.ai_risk} />
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* ===== BUDGET RESULTS ===== */}
        {activeTab === "budget" && budgetData && (
          <div className="mt-8" data-testid="civic-budget-results">
            <div className="grid sm:grid-cols-3 gap-4">
              <Stat label={`${budgetData.fiscal_year} Budget`} value={`$${(budgetData.total_budget_usd / 1_000_000).toFixed(1)}M`} accent="gold" />
              <Stat label="Variance flags" value={budgetData.flagged_count} accent="electric" />
              <div className="card-premium glass">
                <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Composite risk</div>
                <div className="mt-2"><RiskPill risk={budgetData.ai_risk} /></div>
              </div>
            </div>
            <NarrativeBlock title="AuditIQ Budget Narrative" text={budgetData.narrative} testid="civic-budget-narrative" />

            <h3 className="font-display mt-8">Line-item breakdown</h3>
            <div className="table-wrap glass mt-4">
              <table className="w-full text-sm">
                <thead className="border-b border-gold-dim/20">
                  <tr className="text-cream-dim text-xs uppercase tracking-[0.18em]">
                    <th className="text-left p-3">Category</th>
                    <th className="text-right p-3">Current %</th>
                    <th className="text-right p-3">Prior %</th>
                    <th className="text-right p-3">Variance</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-right p-3">AI Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetData.line_items.map((it) => (
                    <tr key={it.category} className="border-b border-gold-dim/10" data-testid={`budget-row-${it.category.toLowerCase().replace(/\s+/g, '-')}`}>
                      <td className="p-3 text-cream">{it.category}</td>
                      <td className="p-3 text-right text-cream">{it.current_pct}%</td>
                      <td className="p-3 text-right text-cream-dim">{it.prior_pct}%</td>
                      <td className={`p-3 text-right ${Math.abs(it.variance_pct) >= 1.5 ? "text-[color:var(--auditiq-warning)]" : "text-cream-dim"}`}>
                        {it.variance_pct > 0 ? "+" : ""}{it.variance_pct}%
                      </td>
                      <td className="p-3 text-right text-cream font-mono">${it.amount_usd.toLocaleString()}</td>
                      <td className="p-3 text-right"><RiskPill risk={it.ai_risk} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ===== RECORDS RESULTS ===== */}
        {activeTab === "records" && recordsData && (
          <div className="mt-8" data-testid="civic-records-results">
            <div className="grid sm:grid-cols-3 gap-4">
              <Stat label="Records found" value={recordsData.records.length} accent="gold" />
              <Stat label="Record types scanned" value={(recordsData.available_record_types || []).length} accent="electric" />
              <div className="card-premium glass">
                <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Composite risk</div>
                <div className="mt-2"><RiskPill risk={recordsData.ai_risk} /></div>
              </div>
            </div>
            <NarrativeBlock title="AuditIQ Records Narrative" text={recordsData.narrative} testid="civic-records-narrative" />

            <h3 className="font-display mt-8">Public records</h3>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {recordsData.records.map((r) => (
                <article key={r.id} className="glass p-5" data-testid={`record-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-electric flex items-center gap-2">
                      <FileText size={12} /> {r.record_type}
                    </div>
                    <RiskPill risk={r.ai_risk} />
                  </div>
                  <h4 className="font-display text-cream mt-2">{r.title}</h4>
                  <div className="text-xs text-cream-dim mt-1">Filed {r.filed} · {r.foia_status}</div>
                  <p className="text-cream-dim text-sm mt-3">{r.summary}</p>
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-electric text-xs mt-3 hover:text-gold"
                  >
                    View source <ArrowRight size={12} />
                  </a>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!searchData && !budgetData && !recordsData && (
          <div className="card-premium glass mt-8 text-center" data-testid="civic-empty-state">
            <Database size={28} className="text-gold mx-auto" />
            <h3 className="font-display mt-3">Run a crawl to see results</h3>
            <p className="text-cream-dim mt-2 text-sm max-w-lg mx-auto">
              Pick a jurisdiction above and run the active engine. Each crawl produces
              an AI-graded narrative and a per-row risk indicator.
            </p>
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="bg-navy-deep border-t border-gold-dim/20 section-y">
        <div className="container-x card-premium glass flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-gold mt-1" />
            <div>
              <h3 className="font-display">Want this on real data?</h3>
              <p className="text-cream-dim text-sm mt-1">Speak with the AuditIQ team about a robots.txt-compliant pilot for your city, county, or special district.</p>
            </div>
          </div>
          <div className="flex gap-3 btn-stack">
            <Link to="/contact" className="contents"><Button className="btn-gold h-12">Request Pilot <ArrowRight size={14} className="ml-2" /></Button></Link>
            <Link to="/portal/login" className="contents"><Button className="btn-ghost-gold h-12">Open Secure Portal</Button></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DemoCivicCrawler;
